// 05a · Dépouillement — la branche la plus lourde du parcours, reprise stricte
// de l'écran V2 (§7.5, P8).
//
// Ce qui vient de la V2 sans négociation, parce que le poste s'appuie dessus
// tous les jours :
//   • le regroupement par opérateur, chaque bloc repliable, la couleur du bloc
//     qui dit d'un coup d'œil s'il reste quelque chose à vérifier ;
//   • les opérateurs SANS badge, isolés en bas : ORIKA n'a pas su lire leur
//     numéro, le parseur leur donne une clé synthétique ≥ 1 000 000 (audit
//     BUG-1) — les mélanger aux badgés ferait croire à des matricules réels ;
//   • le sélecteur trois états, où recliquer l'état actif l'efface ;
//   • la bascule « inclure dans la remise » sur un règlement non conforme :
//     par défaut OUI (absence de clé = true), c'est ce que lit
//     computeRemiseMensuelle pour la remise mensuelle ;
//   • les bons divers à classer, avec les types lus dans les réglages SotCP ;
//   • l'édition d'un paiement par mutate_paiement, qui capture le « avant »
//     côté serveur et écrit la ligne d'audit — aucun client ne peut la forger ;
//   • le signalement caissier, dont l'auteur et l'heure sont posés par le
//     serveur depuis le JWT signé. Il reste ICI, attaché au caissier, et pas
//     dans le fil de collaboration : c'est une observation sur une personne,
//     destinée au responsable, pas un message d'équipe.
//
// Ce que FlashCP n'a pas eu à reprendre : le temps réel. La V2 rebranchait
// `verifications` à la main quand la douchette Honeywell validait un TRP ;
// ici RunProvider écoute déjà `journees` et le blob arrive tout seul.
//
// Ce qui a été CORRIGÉ par rapport à la V2 (et dit dans le code, en place) :
//   • l'écart de caisse ne se colore plus au signe — un excédent est une
//     anomalie exactement comme un manque (@/domain/ecart) ;
//   • une ligne du rapport opérateur sans badge lisible n'est plus perdue ;
//   • effacer un statut de vérification retire vraiment la clé du blob ;
//   • un bloc soldé n'est plus vert s'il porte un écart, un bon divers non
//     classé ou un signalement ;
//   • le montant d'un paiement ajouté à la main accepte enfin la virgule du
//     pavé numérique — « 12,50 » partait à 0,00 € en silence.
//
// Aucune écriture directe : le rapport ne bouge que par ecrireRapport (patch
// local immédiat, RPC derrière, retour en arrière si elle refuse).

import { useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react'
import { isPadPayment, isUnbadgedOperateur, TYPES_REGLEMENT } from 'sotcp-core/csvParser'
import { useRun } from '@/data/RunProvider'
import { useReglages, type TypeBonDivers } from '@/data/reglages'
import * as api from '@/data/api'
import { couleurEcart, fmtEcart, lampeEcart, motEcart, severite } from '@/domain/ecart'
import { fmtEur } from '@/lib/format'
import { Chip, Kpis, Lamp, Modale, Notice, Vide, type Couleur } from '@/ui/primitives'
import { useToast } from '@/ui/Toast'
import DepouillementWizard from './DepouillementWizard'
import type { Rapport } from '@/domain/types'

/** L'aiguillage 05 rend cet écran : c'est son code qui reçoit les signaux de vie. */
const CODE_ETAPE = '05B'

/** BON DIVERS — le seul type de règlement qui exige un classement manuel. */
const REGLEMENT_BON_DIVERS = 14

const TYPES_BD_DEFAUT: TypeBonDivers[] = [
  { value: 'sogec', label: 'Sogec' },
  { value: 'kadeos', label: 'Kadéos' },
]

// ── Lectures défensives du blob ───────────────────────────────────────────────

const tableau = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? (v as Record<string, unknown>[]) : []

const objet = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}

const txt = (v: unknown): string => (v == null ? '' : String(v))

type Statut = 'valide' | 'en_attente' | 'non_conforme'

/**
 * Les statuts d'avant mai 2026 sont encore en base sous leurs noms anglais.
 * Les traduire à la lecture, jamais à l'écriture : set_payment_verification
 * n'accepte que les trois noms français (ou null pour « non vérifié »).
 */
const normStatut = (v: unknown): Statut | null =>
  v === 'confirmed' ? 'valide'
    : v === 'rejected' ? 'non_conforme'
      : v === 'valide' || v === 'en_attente' || v === 'non_conforme' ? v : null

const COULEUR_STATUT: Record<Statut, Couleur> = {
  valide: 'ok', en_attente: 'warn', non_conforme: 'stop',
}

interface Paiement {
  id: string
  ticket: string
  operateur: string
  numeroOperateur: number
  montant: number
  reglementId: number | null
  typeReglement: string
  heure: string
  caisse: string
  reference: string
  /** La ligne d'origine, jamais reconstruite : une réécriture perdrait ses clés inconnues. */
  brut: Record<string, unknown>
}

const lirePaiement = (b: Record<string, unknown>): Paiement => ({
  id: txt(b.id),
  ticket: txt(b.ticket),
  operateur: txt(b.operateur),
  numeroOperateur: Number(b.numeroOperateur) || 0,
  montant: Number(b.montant) || 0,
  reglementId: b.reglementId == null ? null : Number(b.reglementId),
  typeReglement: txt(b.typeReglement),
  heure: txt(b.heure),
  caisse: txt(b.caisse),
  reference: txt(b.reference),
  brut: b,
})

interface OpRapport {
  id: string
  nom: string
  badge: string
  fdc: number
  apport: number
  reglEsp: number
  prelev: number
  prelevCount: number
  prelevIrregular: boolean
  theorique: number
  decl: number
  diff: number
}

const lireOp = (o: Record<string, unknown>): OpRapport => ({
  id: txt(o.id),
  nom: txt(o.nom),
  badge: txt(o.badge),
  fdc: Number(o.fdc) || 0,
  apport: Number(o.apport) || 0,
  reglEsp: Number(o.reglEsp) || 0,
  prelev: Number(o.prelev) || 0,
  prelevCount: Number(o.prelevCount) || 0,
  prelevIrregular: o.prelevIrregular === true,
  theorique: Number(o.theorique) || 0,
  decl: Number(o.decl) || 0,
  diff: Number(o.diff) || 0,
})

interface Signalement { text: string; author?: string; at?: string }

const lireSignalement = (v: unknown): Signalement | null => {
  const o = objet(v)
  const t = typeof o.text === 'string' ? o.text.trim() : ''
  if (!t) return null
  return {
    text: t,
    author: typeof o.author === 'string' ? o.author : undefined,
    at: typeof o.at === 'string' ? o.at : undefined,
  }
}

/**
 * Format V2 de l'horodatage d'un signalement : 08/06 14:20.
 * Fuseau de Paris explicite comme partout ailleurs (lib/format) — le poste est
 * en France, mais une heure qui dépend du réglage du navigateur n'est pas une
 * heure. Une chaîne illisible retombe sur « — » plutôt que « Invalid Date ».
 */
const horodate = (iso: string | undefined): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    timeZone: 'Europe/Paris',
  })
}

/**
 * La V2 coloriait le badge de type par famille (TRP orange, chèque violet…).
 * Ici le violet est réservé à Honeywell, le vert au validé et le rouge au
 * bloquant : détourner ces couleurs pour un libellé de règlement rendrait
 * l'écran illisible. Deux familles seulement gardent une teinte, le nom complet
 * du type reste écrit en toutes lettres comme en V2.
 */
function couleurType(type: string): Couleur {
  const t = type.toUpperCase()
  if (t.includes('TRP') || t.includes('TICKET RESTAURANT')) return 'warn'
  if (t.includes('CARTE') || t.includes('AMEX') || t.includes('CB')) return 'act'
  return 'neutre'
}

/** Le nom court : ORIKA colle le badge entre parenthèses derrière le nom. */
const nomCourt = (nom: string): string => nom.replace(/\s*\(\d+\)\s*$/, '').trim()

/** Clé de rapprochement par le nom, pour les lignes de rapport sans badge. */
const cleNom = (nom: string): string =>
  nomCourt(nom).toUpperCase().replace(/\s+/g, ' ').trim()

interface Operateur {
  num: number
  nom: string
  paiements: Paiement[]
  op: OpRapport | null
}

/** Le compte d'un bloc — calculé une fois dans l'écran, lu par le bloc. */
interface Compte {
  valide: number
  attente: number
  nonConf: number
  /** Jamais touché : ni validé, ni en attente, ni refusé. */
  restant: number
  /** Bons divers de ce caissier encore sans type — ils bloquent la remise. */
  aClasser: number
}

// ── Sélecteur trois états ─────────────────────────────────────────────────────
//
// Les trois tons disent le geste : ok confirme, warn diffère, stop refuse.
// Aucune couleur en dur — `aria-pressed` porte l'état retenu et base.css le
// remplit. Recliquer l'état actif l'efface (convention V2).

const TROIS: Array<{ v: Statut; libelle: string; verbe: string }> = [
  { v: 'valide', libelle: '✓', verbe: 'Valider ce règlement' },
  { v: 'en_attente', libelle: '⏳', verbe: 'Mettre ce règlement en attente' },
  { v: 'non_conforme', libelle: '✗', verbe: 'Refuser ce règlement' },
]

function SelecteurTrois({ statut, figer, changer }: {
  statut: Statut | null
  figer: boolean
  changer: (s: Statut | null) => void
}) {
  return (
    <div style={{ display: 'flex', gap: 3, flexShrink: 0 }} role="group"
         aria-label="Statut de vérification">
      {TROIS.map(({ v, libelle, verbe }) => {
        const actif = statut === v
        return (
          <button key={v} className={`btn ${COULEUR_STATUT[v]} sm`} aria-pressed={actif}
                  disabled={figer} aria-label={verbe}
                  title={actif ? `${verbe} — recliquer pour effacer le statut` : verbe}
                  onClick={() => changer(actif ? null : v)}
                  // Cible confortable : l'écran se clique debout, vite.
                  style={{ minWidth: 34, height: 28, padding: 0, lineHeight: 1 }}>
            {libelle}
          </button>
        )
      })}
    </div>
  )
}

// ── Sélecteur de bons divers ──────────────────────────────────────────────────

function SelecteurBonDivers({ valeur, options, figer, changer }: {
  valeur: string | null
  options: TypeBonDivers[]
  figer: boolean
  changer: (v: string | null) => void
}) {
  const liste = options.length > 0 ? options : TYPES_BD_DEFAUT
  const connu = liste.some((o) => o.value === valeur)
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
      <span className="lbl">Type</span>
      {liste.map(({ value, label }) => {
        const actif = valeur === value
        return (
          <button key={value} className="btn sm" aria-pressed={actif} disabled={figer}
                  title={actif ? `Classer en ${label} — recliquer pour effacer` : `Classer en ${label}`}
                  onClick={() => changer(actif ? null : value)}>
            {label}
          </button>
        )
      })}
      {!valeur && <Chip c="stop">À CLASSER</Chip>}
      {valeur && !connu && <Chip c="warn">({valeur}) — TYPE RETIRÉ</Chip>}
    </div>
  )
}

// ── Suppression en deux temps ─────────────────────────────────────────────────

/**
 * Supprimer une ligne de paiement est audité côté serveur, mais la retrouver
 * demande de ressaisir sept champs à la main : ce n'est pas réversible d'un
 * geste, donc ça se confirme. Deux clics au même endroit valent mieux qu'une
 * modale : rien ne bouge sous le curseur, et l'armement retombe seul.
 */
function BoutonSupprimer({ quoi, figer, supprimer, marge }: {
  quoi: string
  figer: boolean
  supprimer: () => void
  marge?: boolean
}) {
  const [arme, setArme] = useState(false)
  useEffect(() => {
    if (!arme) return
    const t = window.setTimeout(() => setArme(false), 4000)
    return () => window.clearTimeout(t)   // jamais d'écriture après démontage
  }, [arme])

  return (
    <button className={arme ? 'btn stop sm' : 'btn gh sm'} disabled={figer}
            style={marge ? { marginRight: 'auto' } : undefined}
            title={arme ? 'Cliquer à nouveau pour supprimer' : `Supprimer ${quoi}`}
            onClick={() => { if (arme) { supprimer() } else setArme(true) }}>
      {arme ? 'Confirmer la suppression' : 'Supprimer'}
    </button>
  )
}

// ── Panneau Rapport opérateur ─────────────────────────────────────────────────

/** Bascule à deux issues (reçu / languette / signature) : oui confirme, non refuse. */
function Bouton2({ actif, danger, figer, libelle, onClick, children }: {
  actif: boolean
  danger?: boolean
  figer: boolean
  libelle: string
  onClick: () => void
  children: string
}) {
  return (
    <button className={`btn ${danger ? 'stop' : 'ok'} sm`} aria-pressed={actif} disabled={figer}
            aria-label={libelle} title={libelle} onClick={onClick}
            style={{ minWidth: 30, height: 26, padding: 0, lineHeight: 1 }}>
      {children}
    </button>
  )
}

function Chiffre({ k, v, couleur, titre, chip }: {
  k: string; v: string; couleur?: string; titre?: string; chip?: ReactNode
}) {
  return (
    <div className="slot" style={{ padding: '6px 12px' }} title={titre}>
      <span className="lbl">{k}</span>
      {chip}
      <span className="sp" />
      <span className="mo" style={{ fontSize: 12, color: couleur }}>{v}</span>
    </div>
  )
}

/**
 * Le rapport opérateur du caissier, et l'état de ses enveloppes de prélèvement.
 * Le nombre d'enveloppes n'est pas saisi : le parseur le déduit du montant
 * prélevé (une enveloppe = 500 €), et signale le reste non nul comme irrégulier.
 */
function PanneauRapport({ op, etat, seuil, figer, changer }: {
  op: OpRapport
  etat: Record<string, unknown>
  seuil: number
  figer: boolean
  changer: (etatOp: Record<string, unknown>) => void
}) {
  const auDessus = op.decl > seuil
  const env = (i: number): Record<string, unknown> => objet(etat[`prelev_${i}`])
  const poser = (i: number, champ: string, val: unknown) =>
    changer({ ...etat, [`prelev_${i}`]: { ...env(i), [champ]: val } })

  // Le parseur déduit le nombre d'enveloppes du montant (une = 500 €) et
  // ARRONDIT : 200 € prélevés donnent zéro enveloppe alors que l'argent est
  // bien sorti du tiroir. La V2 n'affichait alors aucune ligne à cocher — ni
  // enveloppe, ni « aucun prélèvement » : un panneau muet sur de l'argent
  // sorti. Dès que le montant est non nul on montre au moins une enveloppe ;
  // prelevIrregular dit déjà, juste au-dessus, que le compte est faux.
  const nbEnv = op.prelev > 0 ? Math.max(op.prelevCount, 1) : 0

  // Canari de colonnes ORIKA : l'export donne diff = déclaré − théorique. Si la
  // relation est fausse, c'est que les colonnes ont glissé (l'incident des
  // annulés de juillet a exactement cette forme) — mieux vaut le dire que
  // d'afficher trois nombres qui ne parlent pas de la même chose.
  const incoherent = Math.abs((op.decl - op.theorique) - op.diff) > 0.01

  return (
    <div>
      <div className="card-hd">
        <h3>Rapport opérateur · prélèvements</h3>
      </div>

      <Chiffre k="Fonds de caisse" v={fmtEur(op.fdc)} />
      {op.apport !== 0 && <Chiffre k="Apport" v={fmtEur(op.apport)} />}
      {op.reglEsp > 0 && <Chiffre k="Règlements espèces" v={fmtEur(op.reglEsp)} />}
      {op.prelev > 0 && (
        <Chiffre k="Prélèvements" v={fmtEur(op.prelev)}
                 chip={op.prelevIrregular ? <Chip c="stop">NON MULTIPLE DE 500 €</Chip> : undefined}
                 couleur={op.prelevIrregular ? 'var(--stop)' : undefined} />
      )}
      <Chiffre k="Théorique" v={fmtEur(op.theorique)} />
      <Chiffre k="Déclaré" v={fmtEur(op.decl)}
               chip={auDessus ? <Chip c="warn">AU-DESSUS DU SEUIL</Chip> : undefined}
               couleur={auDessus ? 'var(--warn)' : undefined} />
      {/* L'écart est le chiffre de la ligne : il s'affiche TOUJOURS, nul compris
          — son absence ne dirait pas si le tiroir tombe juste ou si la donnée
          manque. Sa couleur ne dépend jamais de son signe (domain/ecart). */}
      <Chiffre k="Écart" v={fmtEcart(op.diff)} couleur={couleurEcart(op.diff)}
               titre={`Déclaré − théorique · ${motEcart(op.diff)}`} />

      {/* Le commentaire de l'écart se saisit ICI, au moment où on le constate,
          et pas à l'étape 11 : la ressaisir en fin de parcours, deux heures plus
          tard, ferait écrire de mémoire ce qu'on avait sous les yeux. C'est la
          11 qui le RESTITUE, avec le montant et le motif (handoff §5.4).

          Il porte sur la LIGNE d'écart, pas sur le caissier : un même caissier
          peut porter plusieurs postes distincts, et le signalement au
          responsable — juste au-dessus — est autre chose encore. */}
      <CommentaireEcart texte={typeof etat.commentaireEcart === 'string' ? etat.commentaireEcart : ''}
                        figer={figer}
                        changer={(v) => changer({ ...etat, commentaireEcart: v })} />

      {incoherent && (
        <div className="hint" style={{ padding: '6px 12px', color: 'var(--stop)' }}>
          Incohérence de colonnes ORIKA : déclaré − théorique = {fmtEcart(op.decl - op.theorique)},
          mais l’écart importé vaut {fmtEcart(op.diff)}. Contrôler le fichier avant de conclure.
        </div>
      )}

      {auDessus && (
        <div className="hint" style={{ padding: '6px 12px' }}>
          Déclaration au-dessus du seuil de {fmtEur(seuil)} — à signaler au responsable.
        </div>
      )}

      {op.prelevIrregular && (
        <div className="hint" style={{ padding: '6px 12px', color: 'var(--stop)' }}>
          Montant prélevé non multiple de 500 € — le nombre d’enveloppes ci-dessous est
          déduit du montant, il est à recompter à la main.
        </div>
      )}

      {op.prelev > 0 ? (
        Array.from({ length: nbEnv }, (_, i) => {
          const e = env(i)
          const manquant = e.manquant === true
          const nom = nbEnv > 1 ? `Prélèvement ${i + 1}` : 'Prélèvement'
          return (
            <div key={i} className="slot"
                 style={{
                   flexWrap: 'wrap', gap: 8,
                   background: manquant ? 'var(--stop-w)' : undefined,
                 }}>
              <span className="nm" style={{ color: manquant ? 'var(--stop)' : undefined }}>
                {nom}
              </span>
              <button className="btn stop sm" aria-pressed={manquant} disabled={figer}
                      title={`Déclarer ${nom.toLowerCase()} manquant`}
                      onClick={() => poser(i, 'manquant', !manquant)}>
                Manquant
              </button>
              {!manquant && (
                <>
                  <span className="lbl">Reçu</span>
                  <Bouton2 actif={e.recu === true} figer={figer} libelle="Enveloppe reçue"
                           onClick={() => poser(i, 'recu', e.recu === true ? undefined : true)}>✓</Bouton2>
                  <Bouton2 actif={e.recu === false} danger figer={figer} libelle="Enveloppe non reçue"
                           onClick={() => poser(i, 'recu', e.recu === false ? undefined : false)}>✗</Bouton2>
                  <span className="lbl">Languette</span>
                  {/* La languette est présente par défaut : seule son absence se déclare. */}
                  <Bouton2 actif={e.languette !== false} figer={figer} libelle="Languette présente"
                           onClick={() => poser(i, 'languette', true)}>✓</Bouton2>
                  <Bouton2 actif={e.languette === false} danger figer={figer} libelle="Languette absente"
                           onClick={() => poser(i, 'languette', false)}>✗</Bouton2>
                  <span className="lbl">Signature</span>
                  <Bouton2 actif={e.signature === true} figer={figer} libelle="Enveloppe signée"
                           onClick={() => poser(i, 'signature', e.signature === true ? undefined : true)}>✓</Bouton2>
                  <Bouton2 actif={e.signature === false} danger figer={figer} libelle="Enveloppe non signée"
                           onClick={() => poser(i, 'signature', e.signature === false ? undefined : false)}>✗</Bouton2>
                </>
              )}
            </div>
          )
        })
      ) : (
        <div className="empty" style={{ padding: '14px 12px' }}>Aucun prélèvement.</div>
      )}
    </div>
  )
}

// ── Bloc opérateur ────────────────────────────────────────────────────────────

interface PropsBloc {
  o: Operateur
  compte: Compte
  verifs: Record<string, unknown>
  notes: Record<string, unknown>
  bonDivers: Record<string, unknown>
  metas: Record<string, unknown>
  etatsRapport: Record<string, unknown>
  signalement: Signalement | null
  typesBonDivers: TypeBonDivers[]
  seuil: number
  figer: boolean
  aRapport: boolean
  replie: boolean
  basculerReplie: () => void
  filtres: Paiement[]
  changerStatut: (p: Paiement, s: Statut | null) => void
  changerBonDivers: (id: string, v: string | null) => void
  changerMeta: (id: string, patch: Record<string, unknown>) => void
  changerEtatRapport: (opId: string, etatOp: Record<string, unknown>) => void
  ouvrirNote: (p: Paiement) => void
  ouvrirSignalement: (cible: { num: number; nom: string }) => void
  editer: (p: Paiement) => void
  supprimer: (id: string) => void
}

function BlocOperateur(p: PropsBloc) {
  const { o, verifs, figer, compte, filtres } = p
  const court = nomCourt(o.nom)
  const sansBadge = isUnbadgedOperateur(o.num)
  const auDessus = o.op ? o.op.decl > p.seuil : false
  const ecart = o.op?.diff ?? 0
  const sev = severite(ecart)

  // La lampe du bloc dit ce qui RESTE, dans l'ordre où ça compte. Trois fautes
  // corrigées ici :
  //   • un bloc entièrement vérifié passait au vert même avec un écart de
  //     caisse, un bon divers non classé ou un signalement au dossier ;
  //   • un caissier sans aucun règlement non-espèces (mais présent au rapport)
  //     n'atteignait jamais le vert : `paiements.length > 0` le lui interdisait,
  //     alors qu'il n'y avait rien à faire — un « 0 sur 0 » annoncé « en cours » ;
  //   • le vert est réservé à ce qui est conforme ET terminé.
  // Le bon divers non classé est ROUGE, et pas orange : sans émetteur la ligne
  // ne part dans aucune remise mensuelle — c'est de l'argent que le magasin ne
  // récupère pas. La lampe, la puce « N À CLASSER » et le bandeau du haut
  // disent donc tous les trois la même chose.
  const lampe: Couleur =
    compte.restant > 0 ? 'act'
      : p.signalement || sev === 'critique' || compte.aClasser > 0
        || (o.op?.prelevIrregular ?? false) ? 'stop'
        : sev === 'surveille' || auDessus ? 'warn'
          : 'ok'

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-hd">
        <Lamp c={lampe} />
        {/* Replier un caissier fini est LE geste de navigation de l'étape, répété
            à chaque bloc : cible pleine taille, comme les trois états. */}
        <button className="btn gh sm" aria-expanded={!p.replie} onClick={p.basculerReplie}
                aria-label={`${p.replie ? 'Déplier' : 'Replier'} le bloc de ${court}`}
                title={p.replie ? 'Déplier ce caissier' : 'Replier ce caissier'}
                style={{ minWidth: 30, height: 28, padding: 0, lineHeight: 1 }}>
          {p.replie ? '▸' : '▾'}
        </button>
        {sansBadge
          ? <Chip c="warn">BADGE MANQUANT</Chip>
          : <span className="mo" style={{ fontSize: 10, color: 'var(--ink4)' }}>#{o.num}</span>}
        <b style={{ fontSize: 13 }}>{court}</b>
        <span className="sp" />
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
          {p.signalement && <Chip c="stop">SIGNALÉ</Chip>}
          {/* L'écart de caisse vit en tête : replié, c'est le seul chiffre qu'on
              doit encore pouvoir lire. Sa couleur ne dépend pas de son signe. */}
          {o.op && sev !== 'nul' && (
            <Chip c={lampeEcart(ecart)}>ÉCART {fmtEcart(ecart)}</Chip>
          )}
          {auDessus && <Chip c="warn">DÉCL. &gt; {fmtEur(p.seuil)}</Chip>}
          {compte.aClasser > 0 && <Chip c="stop">{compte.aClasser} À CLASSER</Chip>}
          {o.paiements.length > 0 ? (
            <>
              {compte.restant > 0 && <Chip>{compte.restant} À VÉRIFIER</Chip>}
              {compte.valide > 0 && <Chip c="ok">✓ {compte.valide}</Chip>}
              {compte.attente > 0 && <Chip c="warn">⏳ {compte.attente}</Chip>}
              {compte.nonConf > 0 && <Chip c="stop">✗ {compte.nonConf}</Chip>}
            </>
          ) : o.op && <Chip>PAS DE RÈGLEMENT NON-ESPÈCES</Chip>}
          {/* Discret, et pas orange : ce bouton est dans TOUS les en-têtes. Vingt
              boutons orange rendraient l'orange muet, alors que c'est lui qui
              porte l'écart à surveiller et la déclaration au-dessus du seuil.
              Le signalement qui existe, lui, crie déjà en rouge juste dessous. */}
          {!figer && (
            <button className="btn gh sm"
                    onClick={() => p.ouvrirSignalement({ num: o.num, nom: court })}
                    title={p.signalement
                      ? 'Modifier le signalement transmis au responsable'
                      : 'Signaler ce caissier au responsable'}>
              ⚑ {p.signalement ? 'Modifier le signalement' : 'Signaler ce caissier'}
            </button>
          )}
        </div>
      </div>

      {p.signalement && (
        <div className="notice stop" style={{ margin: 0, border: 'none', borderBottom: '1px solid var(--line)' }}>
          <Lamp c="stop" />
          <div className="sp">
            <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{p.signalement.text}</div>
            <div className="hint" style={{ marginTop: 3 }}>
              Signalé au responsable · {p.signalement.author ?? '—'} · {horodate(p.signalement.at)}
            </div>
          </div>
        </div>
      )}

      {!p.replie && (
        <div className={p.aRapport ? 'two' : undefined} style={{ gap: 0, alignItems: 'stretch' }}>
          <div style={{ minWidth: 0 }}>
            {filtres.length === 0
              ? <div className="empty">Aucun paiement pour ce filtre.</div>
              : filtres.map((x) => {
                const st = normStatut(verifs[x.id])
                const note = typeof p.notes[x.id] === 'string' ? (p.notes[x.id] as string) : ''
                const bd = p.bonDivers[x.id]
                return (
                  <div key={x.id} className="li" style={{
                    flexDirection: 'column', gap: 5,
                    background: st ? `var(--${COULEUR_STATUT[st]}-w)` : undefined,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', width: '100%' }}>
                      <Chip c={couleurType(x.typeReglement)}>{x.typeReglement || '—'}</Chip>
                      <span className="mo" style={{ fontSize: 11.5, color: 'var(--ink2)' }}>#{x.ticket}</span>
                      {note && <Chip c="act">NOTE</Chip>}
                      <span className="sp" />
                      {/* Un montant n'est pas un écart : pas de signe « + » sur un
                          encaissement normal. Seule une ligne négative (avoir,
                          annulation) se signale — c'est un sens de flux inhabituel. */}
                      <span className="mo" title={x.montant < 0 ? 'Ligne négative — avoir ou annulation' : undefined}
                            style={{
                              fontSize: 13, fontWeight: 500,
                              color: x.montant < 0 ? 'var(--stop)' : 'var(--ink)',
                            }}>
                        {fmtEur(x.montant)}
                      </span>
                      {/* La zone chaude de la ligne : le geste des onze passages
                          par jour, seul à droite, loin de « Supprimer ». */}
                      <SelecteurTrois statut={st} figer={figer}
                                      changer={(s) => p.changerStatut(x, s)} />
                    </div>

                    <div className="mo" style={{
                      fontSize: 10.5, color: 'var(--ink4)', display: 'flex',
                      alignItems: 'center', gap: 7, flexWrap: 'wrap', width: '100%',
                    }}>
                      <span>
                        {x.heure || '—'} · CAISSE {x.caisse || '—'}
                        {x.reference && x.reference !== 'None' ? ` · RÉF ${x.reference}` : ''}
                      </span>
                      <span className="sp" />
                      {!figer && (
                        <>
                          {/* Un bouton « commenter » par LIGNE, sur toutes les
                              lignes : une ligne parfaitement conforme peut
                              mériter une explication, et la note d'une ligne
                              repassée à « validé » doit rester corrigeable. */}
                          <button className="btn gh sm" onClick={() => p.ouvrirNote(x)}
                                  title={note ? 'Modifier la note' : 'Commenter cette ligne'}>
                            ✎ {note ? 'Modifier la note' : 'Commenter'}
                          </button>
                          <button className="btn gh sm" onClick={() => p.editer(x)}
                                  title="Modifier ce paiement (modification auditée)">
                            Modifier
                          </button>
                          <BoutonSupprimer quoi="ce paiement" figer={figer}
                                           supprimer={() => p.supprimer(x.id)} />
                        </>
                      )}
                    </div>

                    {x.reglementId === REGLEMENT_BON_DIVERS && (
                      <SelecteurBonDivers valeur={typeof bd === 'string' ? bd : null}
                                          options={p.typesBonDivers} figer={figer}
                                          changer={(v) => p.changerBonDivers(x.id, v)} />
                    )}

                    {st === 'non_conforme' && (() => {
                      // Absence de clé = OUI. C'est ce que lit computeRemiseMensuelle :
                      // un non conforme part dans la remise tant qu'on n'a pas dit non.
                      const dansRemise = objet(p.metas[x.id]).addToRemise !== false
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span className="lbl">Inclure dans la remise</span>
                          {[{ v: true, l: 'Oui', c: 'ok' }, { v: false, l: 'Non', c: 'stop' }]
                            .map(({ v, l, c }) => (
                              <button key={l} className={`btn ${c} sm`} aria-pressed={dansRemise === v}
                                      disabled={figer}
                                      title={v ? 'Porter ce règlement à la remise mensuelle'
                                        : 'Écarter ce règlement de la remise mensuelle'}
                                      onClick={() => p.changerMeta(x.id, { addToRemise: v })}>
                                {l}
                              </button>
                            ))}
                        </div>
                      )
                    })()}

                    {note && (
                      <div style={{
                        fontSize: 12, color: 'var(--ink2)', fontStyle: 'italic',
                        padding: '5px 9px', background: 'var(--act-w)',
                        borderLeft: '2px solid var(--act)', width: '100%',
                      }}>
                        « {note} »
                      </div>
                    )}
                  </div>
                )
              })}
          </div>

          {p.aRapport && (
            <div style={{ borderLeft: '1px solid var(--line)', background: 'var(--surface2)' }}>
              {o.op
                ? <PanneauRapport op={o.op} etat={objet(p.etatsRapport[o.op.id])} seuil={p.seuil}
                                  figer={figer}
                                  changer={(e) => { if (o.op) p.changerEtatRapport(o.op.id, e) }} />
                : <div className="empty">Pas dans le rapport opérateur.</div>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Un commentaire libre sur la ligne d'écart. Il s'ouvre au clic et s'enregistre
 * quand on le quitte : l'écran du dépouillement enchaîne quarante caissiers, il
 * ne peut pas s'offrir une modale par ligne.
 */
function CommentaireEcart({ texte, figer, changer }: {
  texte: string
  figer: boolean
  changer: (v: string) => void
}) {
  const [ouvert, setOuvert] = useState(texte !== '')
  const [brouillon, setBrouillon] = useState(texte)
  // Une reprise depuis un autre poste doit se voir, sauf pendant qu'on tape.
  const [edite, setEdite] = useState(false)
  const affiche = edite ? brouillon : texte

  if (!ouvert) {
    return figer ? null : (
      <div style={{ padding: '4px 12px 8px' }}>
        <button className="btn gh sm" onClick={() => { setBrouillon(''); setOuvert(true) }}
                title="Expliquer cet écart — le commentaire est restitué à l’étape 11">
          ✎ Commenter l’écart
        </button>
      </div>
    )
  }

  return (
    <div style={{ padding: '4px 12px 9px' }}>
      <div className="lbl" style={{ marginBottom: 4 }}>Commentaire de l’écart</div>
      <textarea
        className="fld" rows={2} disabled={figer} value={affiche}
        aria-label="Commentaire de l’écart de caisse"
        placeholder="Ce qui explique cet écart — repris tel quel à l’étape 11."
        onFocus={() => { setBrouillon(texte); setEdite(true) }}
        onChange={(e) => setBrouillon(e.target.value)}
        onBlur={() => {
          setEdite(false)
          if (brouillon !== texte) changer(brouillon)
          if (brouillon.trim() === '') setOuvert(false)
        }}
      />
    </div>
  )
}

// ── Modale note ───────────────────────────────────────────────────────────────

function ModaleNote({ paiement, note, fermer, enregistrer }: {
  paiement: Paiement
  note: string
  fermer: () => void
  enregistrer: (texte: string) => void
}) {
  const [texte, setTexte] = useState(note)
  return (
    <Modale titre={`Note — ticket #${paiement.ticket}`}
            sous={`${nomCourt(paiement.operateur)} · ${paiement.typeReglement}`}
            fermer={fermer}>
      <textarea rows={3} autoFocus value={texte} onChange={(e) => setTexte(e.target.value)}
                placeholder="Ajoute une note…" />
      <div className="btns">
        <button className="btn" onClick={fermer}>Annuler</button>
        <button className="btn pri" onClick={() => { enregistrer(texte); fermer() }}>
          Enregistrer la note
        </button>
      </div>
    </Modale>
  )
}

// ── Modale signalement caissier ───────────────────────────────────────────────

function ModaleSignalement({ cible, signalement, fermer, enregistrer }: {
  cible: { num: number; nom: string }
  signalement: Signalement | null
  fermer: () => void
  enregistrer: (texte: string) => void
}) {
  const [texte, setTexte] = useState(signalement?.text ?? '')
  const [enCours, setEnCours] = useState(false)
  const propre = texte.trim()
  const envoyer = (valeur: string) => {
    if (enCours) return           // le double-clic ne repart pas une seconde fois
    setEnCours(true)
    enregistrer(valeur)
    fermer()
  }
  return (
    <Modale titre={`Signalement — #${cible.num} ${cible.nom}`}
            sous="Message transmis au responsable. L’auteur et l’heure sont posés par le serveur."
            fermer={fermer}>
      <textarea rows={4} autoFocus maxLength={2000} value={texte}
                onChange={(e) => setTexte(e.target.value)}
                placeholder="Ex. : écart récurrent de tiroir, comportement à surveiller…" />
      {signalement?.at && (
        <div className="hint" style={{ marginBottom: 10 }}>
          Dernière modification : {signalement.author ?? '—'} · {horodate(signalement.at)}
        </div>
      )}
      <div className="btns">
        {signalement && (
          <BoutonSupprimer quoi="le signalement" figer={enCours} marge
                           supprimer={() => envoyer('')} />
        )}
        <button className="btn" onClick={fermer}>Annuler</button>
        <button className="btn pri" disabled={!propre} data-encours={enCours ? 'true' : undefined}
                onClick={() => envoyer(propre)}>
          Enregistrer le signalement
        </button>
      </div>
    </Modale>
  )
}

// ── Modale édition d'un paiement ──────────────────────────────────────────────

function ModalePaiement({ paiement, ops, dateJournee, fermer, enregistrer, supprimer }: {
  paiement: Paiement | null
  ops: OpRapport[]
  dateJournee: string
  fermer: () => void
  enregistrer: (apres: Record<string, unknown>) => void
  supprimer: (() => void) | null
}) {
  const b = paiement?.brut ?? {}
  const [f, setF] = useState({
    ticket: txt(b.ticket),
    operateur: txt(b.operateur),
    numeroOperateur: b.numeroOperateur == null ? '' : String(b.numeroOperateur),
    montant: b.montant == null ? '' : String(b.montant),
    reglementId: b.reglementId == null ? '2' : String(b.reglementId),
    heure: txt(b.heure),
    caisse: txt(b.caisse),
    reference: txt(b.reference),
  })
  const [enCours, setEnCours] = useState(false)
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }))

  /**
   * Le montant se tape au pavé numérique, et la touche du pavé est une VIRGULE.
   * Le champ était en `type="number"` : le navigateur refuse la virgule et
   * renvoie une valeur VIDE — « 12,50 » repartait à 0,00 € sans un mot, sur une
   * ligne pourtant auditée. Champ texte + clavier décimal : la virgule arrive
   * jusqu'ici, et c'est nous qui convertissons.
   */
  // La chaîne entière doit être un nombre : parseFloat seul accepte le préfixe
  // et lit « 12,5,5 » comme 12,50 — une troncature muette sur une ligne auditée.
  const lireMontant = (s: string): number => {
    const n = s.replace(/[\s\u00A0€]/g, '').replace(',', '.')
    return /^-?(\d+\.?\d*|\.\d+)$/.test(n) ? parseFloat(n) : NaN
  }
  const montant = lireMontant(f.montant)
  const montantOk = Number.isFinite(montant)

  /** Le badge que la liste déroulante colle derrière le nom : « DUPONT (123) ». */
  const badgeDuNom = (v: string): string | undefined => /\((\d+)\)\s*$/.exec(v)?.[1]

  const soumettre = () => {
    if (enCours || !montantOk) return   // une modification auditée ne part qu'une fois
    setEnCours(true)
    const rid = parseInt(f.reglementId, 10)
    const nom = TYPES_REGLEMENT.find((t) => t.id === rid)?.nom ?? ''
    const badge = badgeDuNom(f.operateur)
    enregistrer({
      ...b,
      id: txt(b.id) || `pmt_manual_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      ticket: f.ticket.trim(),
      operateur: f.operateur.trim(),
      // Filet : un n° laissé vide alors que le nom porte son badge enverrait la
      // ligne sur l'opérateur 0, dans un bloc fantôme que personne ne dépouille.
      numeroOperateur: parseInt(f.numeroOperateur, 10)
        || (badge ? parseInt(badge, 10) : 0),
      montant,
      reglementId: rid,
      typeReglement: nom,
      heure: f.heure,
      caisse: f.caisse.trim(),
      reference: f.reference.trim(),
      // La date de la JOURNÉE, pas celle du jour : un dépouillement fait le
      // lendemain rangerait sinon la ligne ajoutée dans la mauvaise journée.
      date: txt(b.date) || dateJournee,
    })
    fermer()
  }

  const champ = (cle: keyof typeof f, libelle: string, extra?: Record<string, unknown>) => (
    <label style={{ display: 'block' }}>
      <span className="lbl">{libelle}</span>
      <input value={f[cle]} onChange={(e) => set(cle, e.target.value)} {...extra} />
    </label>
  )

  return (
    <Modale large titre={paiement ? 'Modifier le paiement' : 'Ajouter un paiement'}
            sous="Toute modification est auditée dans paiements_historique, avec l’état d’avant capturé par le serveur."
            fermer={fermer}>
      {/* Entrée valide, Échap ferme (Modale). La touche n'est captée que dans
          cette modale, et seulement depuis un champ d'une ligne : aucun
          raccourci global n'est posé sur la page. */}
      <div onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault()
          soumettre()
        }
      }}>
        <label style={{ display: 'block' }}>
          <span className="lbl">Type de règlement</span>
          <select value={f.reglementId} onChange={(e) => set('reglementId', e.target.value)}>
            {TYPES_REGLEMENT.map((t) => <option key={t.id} value={t.id}>({t.id}) {t.nom}</option>)}
          </select>
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {/* Le champ qu'on vient chercher : le n° de ticket quand on ajoute une
              ligne oubliée, le montant quand on corrige une ligne existante. */}
          {champ('ticket', 'N° ticket', { placeholder: 'ex : 123456', autoFocus: !paiement })}
          {champ('montant', 'Montant (€)', {
            inputMode: 'decimal', placeholder: '0,00', autoFocus: !!paiement,
            'aria-invalid': montantOk ? undefined : true,
          })}
          {champ('operateur', 'Opérateur', {
            placeholder: 'Nom (badge)', list: 'dep-ops',
            // Choisir « DUPONT (123) » dans la liste remplit le n° au passage :
            // c'est lui, et pas le nom, qui décide du bloc où la ligne atterrit.
            onChange: (e: ChangeEvent<HTMLInputElement>) => {
              const v = e.target.value
              const b = badgeDuNom(v)
              setF((x) => ({
                ...x, operateur: v,
                numeroOperateur: b && !x.numeroOperateur.trim()
                  ? String(parseInt(b, 10)) : x.numeroOperateur,
              }))
            },
          })}
          {champ('numeroOperateur', 'N° opérateur', { type: 'number', placeholder: '0' })}
          {champ('caisse', 'Caisse', { placeholder: 'ex : 001' })}
          {champ('heure', 'Heure', { placeholder: 'HH:MM:SS' })}
        </div>
        {f.montant.trim() !== '' && !montantOk && (
          <div className="hint" style={{ color: 'var(--stop)' }}>
            Montant illisible — chiffres, virgule ou point (ex. 12,50).
          </div>
        )}
        <datalist id="dep-ops">
          {ops.map((o) => <option key={o.id} value={`${o.nom} (${o.badge})`} />)}
        </datalist>
        {champ('reference', 'Référence (optionnel)', { placeholder: 'ex : réf bancaire' })}
      </div>

      <div className="btns">
        {supprimer && (
          <BoutonSupprimer quoi="ce paiement" figer={enCours} marge
                           supprimer={() => { supprimer(); fermer() }} />
        )}
        <button className="btn" onClick={fermer}>Annuler</button>
        <button className="btn pri" disabled={!montantOk}
                title={montantOk ? undefined : 'Saisir d’abord le montant'}
                data-encours={enCours ? 'true' : undefined} onClick={soumettre}>
          {paiement ? 'Enregistrer le paiement' : 'Ajouter le paiement'}
        </button>
      </div>
    </Modale>
  )
}

// ── L'écran ───────────────────────────────────────────────────────────────────

const FILTRES_STATUT: Array<{ v: 'all' | 'pending' | Statut; l: string; t: string }> = [
  { v: 'all', l: 'Tous', t: 'Afficher tous les règlements' },
  { v: 'pending', l: 'Non vérifiés', t: 'N’afficher que les règlements jamais statués' },
  { v: 'valide', l: 'Validés', t: 'N’afficher que les règlements validés' },
  { v: 'en_attente', l: 'En attente', t: 'N’afficher que les règlements mis en attente' },
  { v: 'non_conforme', l: 'Non conformes', t: 'N’afficher que les règlements refusés' },
]

export default function Depouillement({ rapport, lectureSeule }:
{ rapport: Rapport | null; lectureSeule: boolean }) {
  const { date, ecrireRapport, majRapport } = useRun()
  const reglages = useReglages()
  const toast = useToast()

  const [clavier, setClavier] = useState(false)
  const [filtreStatut, setFiltreStatut] = useState<'all' | 'pending' | Statut>('all')
  const [filtreType, setFiltreType] = useState<'all' | number>('all')
  const [seulementActifs, setSeulementActifs] = useState(true)
  const [note, setNote] = useState<Paiement | null>(null)
  const [signalement, setSignalement] = useState<{ num: number; nom: string } | null>(null)
  const [edition, setEdition] = useState<Paiement | 'nouveau' | null>(null)
  const [replies, setReplies] = useState<ReadonlySet<number>>(() => new Set<number>())
  // Rémanence : sous un filtre, la ligne qu'on vient de statuer cesserait de le
  // satisfaire et disparaîtrait SOUS le curseur, décalant toute la liste — le
  // clic suivant tomberait sur la ligne d'à côté. Elle reste donc affichée,
  // avec sa nouvelle couleur, jusqu'au prochain changement de filtre.
  const [remanents, setRemanents] = useState<ReadonlySet<string>>(() => new Set<string>())

  // ── Ce que dit le blob ────────────────────────────────────────────────────
  const bruts = useMemo(() => tableau(rapport?.payments), [rapport])
  const paiements = useMemo(() => bruts.map(lirePaiement), [bruts])
  const ops = useMemo(() => tableau(rapport?.rapportOps).map(lireOp), [rapport])
  const verifs = useMemo(() => objet(rapport?.verifications), [rapport])
  const notes = useMemo(() => objet(rapport?.notes), [rapport])
  const bonDivers = useMemo(() => objet(rapport?.bonDiversTypes), [rapport])
  const metas = useMemo(() => objet(rapport?.paymentMeta), [rapport])
  const etatsRapport = useMemo(() => objet(rapport?.rapportState), [rapport])
  const signalements = useMemo(() => objet(rapport?.operatorReports), [rapport])

  // ── Les écritures ─────────────────────────────────────────────────────────
  // Aucune file d'attente locale : ecrireRapport pose le patch tout de suite,
  // envoie la RPC, et remet l'état d'avant si elle refuse. La V2 accumulait ses
  // patches sur 400 ms parce qu'elle réécrivait le blob entier ; ici chaque
  // écriture porte sa carte complète et la fusion est atomique côté serveur.
  //
  // Chaque écrivain reteste `lectureSeule` : les commandes sont déjà coupées à
  // l'affichage, mais une modale ouverte survit à la validation de la branche
  // depuis un autre onglet. Le serveur refuserait (journee_locked) — autant ne
  // pas afficher un patch optimiste qu'on va reprendre. Convention tenue à
  // l'identique dans DepouillementWizard.
  const patcher = (libelle: string, patch: Record<string, unknown>) => {
    if (lectureSeule) return
    void ecrireRapport(libelle, patch, () => api.patcherRapport(date, patch), CODE_ETAPE)
      .catch(() => { /* le message est déjà passé par le toast du provider */ })
  }

  /**
   * Poser un statut passe par set_payment_verification (fusion côté serveur).
   * L'EFFACER ne peut pas : le garde de la RPC est `p_status NOT IN (…)`, qui
   * laisse passer NULL (SQL à trois valeurs) et écrit alors `{ id: null }` — la
   * clé reste présente, et tout ce qui compte la PRÉSENCE de la clé (le
   * « x / y vérifiés » de l'enveloppe, domain/derive) continue de compter le
   * règlement comme vu. On retire donc la clé en réécrivant la carte entière,
   * comme le font déjà les notes et les bons divers ici même.
   */
  const changerStatut = (p: Paiement, st: Statut | null) => {
    if (lectureSeule) return
    if (filtreStatut !== 'all') {
      setRemanents((s) => new Set(s).add(p.id))
    }
    if (st === null) {
      // patch_rapport_merge est un `rapport || p_patch` : la carte envoyée
      // REMPLACE `verifications` en entier. On en profite pour retirer aussi
      // les `{ id: null }` que la V2 a laissés à chaque effacement — une journée
      // déjà polluée annonce sinon, pour toujours, des règlements « vérifiés »
      // que personne n'a regardés. Le premier décochage la répare.
      // Les statuts anglais d'avant mai 2026 repartent tels quels : on ne
      // traduit jamais à l'écriture.
      const maj: Record<string, unknown> = {}
      for (const [id, v] of Object.entries(verifs)) {
        if (id !== p.id && v != null) maj[id] = v
      }
      patcher('effacement d’un statut de règlement', { verifications: maj })
      return
    }
    const maj = { ...verifs, [p.id]: st }
    void ecrireRapport('vérification d’un règlement', { verifications: maj },
      () => api.enregistrerVerification(date, p.id, st), CODE_ETAPE).catch(() => {})
  }

  const changerNote = (id: string, texte: string) =>
    patcher('note sur un règlement', { notes: { ...notes, [id]: texte } })

  const changerBonDivers = (id: string, v: string | null) =>
    patcher('classement d’un bon divers', { bonDiversTypes: { ...bonDivers, [id]: v } })

  const changerMeta = (id: string, patch: Record<string, unknown>) =>
    patcher('remise d’un règlement non conforme',
      { paymentMeta: { ...metas, [id]: { ...objet(metas[id]), ...patch } } })

  const changerEtatRapport = (opId: string, etatOp: Record<string, unknown>) =>
    patcher('enveloppes de prélèvement', { rapportState: { ...etatsRapport, [opId]: etatOp } })

  /**
   * Signalement caissier : l'auteur et l'heure sont posés par le serveur depuis
   * le JWT signé — le client ne les fournit jamais, l'attribution ne peut pas
   * être forgée. On affiche une heure provisoire, puis on prend la carte que la
   * RPC renvoie, qui fait foi.
   */
  const changerSignalement = (num: number, texte: string) => {
    if (lectureSeule) return
    const cle = String(num)
    const propre = texte.trim()
    if (propre.length > 2000) { toast('MESSAGE TROP LONG — 2000 CARACTÈRES AU PLUS', true); return }
    const optimiste = { ...signalements }
    if (propre) optimiste[cle] = { ...objet(signalements[cle]), text: propre, at: new Date().toISOString() }
    else delete optimiste[cle]
    void ecrireRapport('signalement caissier', { operatorReports: optimiste }, async () => {
      const carte = await api.enregistrerSignalement(date, cle, propre)
      majRapport({ operatorReports: carte })
    }, CODE_ETAPE).catch(() => {})
  }

  const enregistrerPaiement = (apres: Record<string, unknown>) => {
    if (lectureSeule) return
    const id = txt(apres.id)
    // mutate_paiement refuse un id vide (payment_id_required) et exige que
    // p_after.id soit exactement p_payment_id (id_mismatch) : les deux sortent
    // du même txt(apres.id), donc l'accord est structurel.
    if (!id) { toast('PAIEMENT SANS IDENTIFIANT — MODIFICATION REFUSÉE', true); return }
    const existe = bruts.some((b) => txt(b.id) === id)
    const liste = existe ? bruts.map((b) => (txt(b.id) === id ? apres : b)) : [...bruts, apres]
    void ecrireRapport(existe ? 'modification d’un paiement' : 'ajout d’un paiement',
      { payments: liste },
      () => api.muterPaiement(date, existe ? 'edit' : 'add', id, apres), CODE_ETAPE).catch(() => {})
  }

  const supprimerPaiement = (id: string) => {
    if (lectureSeule) return
    // La V2 supprimait au premier clic. Le geste est audité (paiements_historique
    // garde le « avant ») mais pas défaisable d'un clic : le bouton s'arme, il ne
    // supprime qu'au second. Aucune modale — rien ne doit bouger sous le curseur.
    if (!bruts.some((b) => txt(b.id) === id)) return
    void ecrireRapport('suppression d’un paiement',
      { payments: bruts.filter((b) => txt(b.id) !== id) },
      () => api.muterPaiement(date, 'delete', id, null), CODE_ETAPE).catch(() => {})
  }

  // ── Les regroupements ─────────────────────────────────────────────────────
  // Les PAD sortent d'ici : ils ont leur propre étape (03) et leurs quatre
  // issues. isPadPayment reste la source unique de la question.
  const nonPad = useMemo(
    () => paiements.filter(
      (p) => !isPadPayment({ reglementId: p.reglementId, typeReglement: p.typeReglement })),
    [paiements])

  /**
   * Les blocs, et les lignes de rapport qui n'ont trouvé personne.
   *
   * Le rattachement se faisait par `parseInt(badge)` seul : une ligne de rapport
   * dont ORIKA n'a pas su lire le matricule donne NaN, ne rejoignait aucun bloc
   * et DISPARAISSAIT de l'écran — avec son écart de caisse et ses enveloppes de
   * prélèvement, que plus personne ne contrôlait. Même sort pour la seconde
   * ligne d'un badge en double, que `find` n'atteignait jamais.
   * On rattache donc par le nom ce que le badge ne dit pas, et ce qui reste
   * orphelin s'affiche à part, en clair, plutôt que d'être perdu en silence.
   */
  const { operateurs, orphelins } = useMemo(() => {
    const groupes = new Map<number, Paiement[]>()
    for (const p of nonPad) {
      const l = groupes.get(p.numeroOperateur)
      if (l) l.push(p)
      else groupes.set(p.numeroOperateur, [p])
    }

    const parNum = new Map<number, OpRapport>()
    const aRattacher: OpRapport[] = []
    for (const o of ops) {
      const n = parseInt(o.badge, 10)
      if (!Number.isNaN(n) && !parNum.has(n)) parNum.set(n, o)
      else aRattacher.push(o)
    }

    const numParNom = new Map<string, number>()
    for (const [num, liste] of groupes) {
      const cle = cleNom(liste[0]?.operateur ?? '')
      if (cle && !numParNom.has(cle)) numParNom.set(cle, num)
    }

    const restants: OpRapport[] = []
    for (const o of aRattacher) {
      const num = numParNom.get(cleNom(o.nom))
      if (num !== undefined && !parNum.has(num)) parNum.set(num, o)
      else restants.push(o)
    }

    const nums = new Set<number>([...groupes.keys(), ...parNum.keys()])
    const liste: Operateur[] = [...nums].sort((a, b) => a - b).map((num) => {
      const paies = groupes.get(num) ?? []
      const op = parNum.get(num) ?? null
      return { num, nom: paies[0]?.operateur || op?.nom || String(num), paiements: paies, op }
    })
    return { operateurs: liste, orphelins: restants }
  }, [nonPad, ops])

  // « Caissier actif » : il a pris AU MOINS un paiement (PAD compris) ou porte
  // un règlement espèces non nul au rapport. Le test espèces est essentiel —
  // un caissier qui n'a fait que du liquide n'a aucune ligne dans `payments`,
  // le filtrer sur les paiements seuls le ferait disparaître à tort.
  const numsActifs = useMemo(() => new Set(paiements.map((p) => p.numeroOperateur)), [paiements])
  const estActif = (o: Operateur) => numsActifs.has(o.num) || (o.op ? o.op.reglEsp !== 0 : false)
  const inactifs = operateurs.filter((o) => !estActif(o)).length
  const visibles = seulementActifs ? operateurs.filter(estActif) : operateurs

  const filtrer = (liste: Paiement[]) => liste.filter((p) => {
    const typeOk = filtreType === 'all' || p.reglementId === filtreType
    const st = normStatut(verifs[p.id])
    const statutOk = filtreStatut === 'all'
      || (filtreStatut === 'pending' && st === null)
      || filtreStatut === st
    return typeOk && (statutOk || remanents.has(p.id))
  })

  /** Le compte d'un bloc : ce qui reste, et ce qui n'est pas classé. */
  const compter = (o: Operateur): Compte => {
    let valide = 0, attente = 0, nonConf = 0, aClasser = 0
    for (const x of o.paiements) {
      const st = normStatut(verifs[x.id])
      if (st === 'valide') valide++
      else if (st === 'en_attente') attente++
      else if (st === 'non_conforme') nonConf++
      if (x.reglementId === REGLEMENT_BON_DIVERS && !bonDivers[x.id]) aClasser++
    }
    return { valide, attente, nonConf, restant: o.paiements.length - valide - attente - nonConf, aClasser }
  }

  // Un bloc sans paiement retenu par le filtre ET sans ligne au rapport
  // opérateur n'a rien à montrer — convention V2. La décision se prend ici, et
  // non dans le bloc : c'est le seul endroit d'où l'on peut voir qu'il ne reste
  // RIEN à l'écran, et proposer d'en sortir plutôt que d'afficher le vide.
  const rendus = visibles
    .map((o) => ({ o, filtres: filtrer(o.paiements), compte: compter(o) }))
    .filter(({ o, filtres }) => filtres.length > 0 || o.op !== null)
  const badges = rendus.filter(({ o }) => !isUnbadgedOperateur(o.num))
  const sansBadge = rendus.filter(({ o }) => isUnbadgedOperateur(o.num))

  // Les compteurs se lisent sur les paiements non-PAD eux-mêmes, pas sur les
  // valeurs de `verifications` comme en V2 : cette carte garde les identifiants
  // des PAD et des lignes supprimées, ce qui donnait un « non vérifiés »
  // négatif.
  const stats = useMemo(() => {
    let valide = 0, nonConf = 0, attente = 0, montant = 0
    for (const p of nonPad) {
      montant += p.montant
      const st = normStatut(verifs[p.id])
      if (st === 'valide') valide++
      else if (st === 'non_conforme') nonConf++
      else if (st === 'en_attente') attente++
    }
    return {
      total: nonPad.length, valide, nonConf, attente,
      restant: nonPad.length - valide - nonConf - attente, montant,
    }
  }, [nonPad, verifs])

  // Les écarts de caisse du jour. Le cumul est pris EN VALEUR ABSOLUE : un
  // manque de 50 € et un excédent de 50 € ne s'annulent pas, ce sont deux
  // anomalies. La couleur suit le pire écart, jamais son sens.
  const ecarts = useMemo(() => {
    let nb = 0, cumul = 0, pire = 0
    for (const o of ops) {
      const a = Math.abs(o.diff)
      if (severite(o.diff) !== 'nul') { nb++; cumul += a }
      if (a > pire) pire = a
    }
    return { nb, cumul, pire }
  }, [ops])

  const nonClasses = nonPad.filter(
    (p) => p.reglementId === REGLEMENT_BON_DIVERS && !bonDivers[p.id]).length

  const aRapport = ops.length > 0

  /** Changer de filtre remet la rémanence à zéro : nouvelle passe, nouvelle liste. */
  const poserFiltreStatut = (v: 'all' | 'pending' | Statut) => {
    setFiltreStatut(v)
    setRemanents(new Set<string>())
  }
  const poserFiltreType = (v: 'all' | number) => {
    setFiltreType(v)
    setRemanents(new Set<string>())
  }

  const toutReplier = () => setReplies(new Set(rendus.map(({ o }) => o.num)))
  const toutDeplier = () => setReplies(new Set<number>())
  const basculerReplie = (num: number) => setReplies((s) => {
    const n = new Set(s)
    if (!n.delete(num)) n.add(num)
    return n
  })

  // Le wizard clavier reste un mode alternatif : ni promu, ni supprimé (§7.5).
  if (clavier) {
    return <DepouillementWizard rapport={rapport} lectureSeule={lectureSeule}
                                onQuitter={() => setClavier(false)} />
  }

  if (paiements.length === 0 && ops.length === 0) {
    return (
      <Vide>
        Rien à dépouiller : ni règlements ni rapport opérateur dans le blob du jour.
        L’étape 01 · Import les charge.
      </Vide>
    )
  }

  const rendreBloc = ({ o, filtres, compte }:
  { o: Operateur; filtres: Paiement[]; compte: Compte }) => (
    <BlocOperateur key={o.num} o={o} compte={compte} filtres={filtres}
                   verifs={verifs} notes={notes} bonDivers={bonDivers}
                   metas={metas} etatsRapport={etatsRapport}
                   signalement={lireSignalement(signalements[String(o.num)])}
                   typesBonDivers={reglages.typesBonDivers}
                   seuil={reglages.seuilDeclarationEspeces}
                   figer={lectureSeule} aRapport={aRapport}
                   replie={replies.has(o.num)} basculerReplie={() => basculerReplie(o.num)}
                   changerStatut={changerStatut} changerBonDivers={changerBonDivers}
                   changerMeta={changerMeta} changerEtatRapport={changerEtatRapport}
                   ouvrirNote={setNote} ouvrirSignalement={setSignalement}
                   editer={setEdition} supprimer={supprimerPaiement} />
  )

  return (
    <>
      {note && (
        <ModaleNote paiement={note}
                    note={typeof notes[note.id] === 'string' ? (notes[note.id] as string) : ''}
                    fermer={() => setNote(null)}
                    enregistrer={(t) => changerNote(note.id, t)} />
      )}
      {signalement && (
        <ModaleSignalement cible={signalement}
                           signalement={lireSignalement(signalements[String(signalement.num)])}
                           fermer={() => setSignalement(null)}
                           enregistrer={(t) => changerSignalement(signalement.num, t)} />
      )}
      {edition !== null && (
        <ModalePaiement paiement={edition === 'nouveau' ? null : edition}
                        ops={ops} dateJournee={date}
                        fermer={() => setEdition(null)}
                        enregistrer={enregistrerPaiement}
                        supprimer={edition === 'nouveau'
                          ? null
                          : () => supprimerPaiement(edition.id)} />
      )}

      <Kpis items={[
        { k: 'Règlements', v: stats.total, s: fmtEur(stats.montant) },
        { k: 'Validés', v: stats.valide, couleur: stats.valide ? 'var(--ok)' : undefined },
        { k: 'En attente', v: stats.attente, couleur: stats.attente ? 'var(--warn)' : undefined },
        { k: 'Non conformes', v: stats.nonConf, couleur: stats.nonConf ? 'var(--stop)' : undefined },
        { k: 'Non vérifiés', v: stats.restant, couleur: stats.restant ? 'var(--act)' : undefined },
        ...(aRapport ? [{
          k: 'Écarts caisse',
          v: ecarts.nb,
          s: `cumul ${fmtEur(ecarts.cumul)} en valeur absolue`,
          couleur: couleurEcart(ecarts.pire),
        }] : []),
      ]} />

      {nonClasses > 0 && (
        // Rouge, comme la puce et la lampe du bloc : sans type, la ligne ne part
        // dans aucune remise. (`c="act"` n'aurait de surcroît rien peint —
        // base.css n'a pas de règle `.notice.act`.)
        <Notice c="stop" action={filtreType === REGLEMENT_BON_DIVERS ? undefined : (
          <button className="btn sm" onClick={() => poserFiltreType(REGLEMENT_BON_DIVERS)}>
            Ne montrer que les bons divers
          </button>
        )}>
          <b>{nonClasses} BON DIVERS non classé{nonClasses > 1 ? 's' : ''}</b> — le type
          (Sogec / Kadéos / …) commande la remise mensuelle. Sans lui, la ligne n’est
          rattachée à aucun émetteur.
        </Notice>
      )}

      {/* Filtres et outils */}
      <div className="card" style={{
        padding: 9, marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }} role="group"
             aria-label="Filtrer par statut">
          {FILTRES_STATUT.map(({ v, l, t }) => (
            <button key={v} className="btn sm" aria-pressed={filtreStatut === v} title={t}
                    onClick={() => poserFiltreStatut(v)}>
              {l}
            </button>
          ))}
        </div>

        <select className="fld" style={{ width: 'auto', minWidth: 210 }}
                aria-label="Filtrer par type de règlement"
                value={filtreType === 'all' ? 'all' : String(filtreType)}
                onChange={(e) => poserFiltreType(
                  e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))}>
          <option value="all">Tous les types de règlement</option>
          {TYPES_REGLEMENT.map((t) => <option key={t.id} value={t.id}>({t.id}) {t.nom}</option>)}
        </select>

        <span className="sp" />

        {inactifs > 0 && (
          <span className="hint">
            {inactifs} inactif{inactifs > 1 ? 's' : ''} {seulementActifs ? 'masqué' : 'affiché'}
            {inactifs > 1 ? 's' : ''}
          </span>
        )}
        <button className="btn sm" aria-pressed={seulementActifs}
                title="N’afficher que les caissiers ayant pris un paiement ou un règlement espèces. Masque les caisses inoccupées et les comptes système."
                onClick={() => setSeulementActifs((v) => !v)}>
          Caissiers actifs
        </button>
        {rendus.length > 1 && (
          <button className="btn gh sm"
                  title={replies.size >= rendus.length
                    ? 'Déplier tous les caissiers' : 'Replier tous les caissiers'}
                  onClick={replies.size >= rendus.length ? toutDeplier : toutReplier}>
            {replies.size >= rendus.length ? 'Tout déplier' : 'Tout replier'}
          </button>
        )}
        <button className="btn sm" onClick={() => setClavier(true)}
                title="Dépouiller au clavier : saisie des chèques et scan des TRP">
          Passer au mode clavier
        </button>
        {!lectureSeule && (
          <button className="btn sm" onClick={() => setEdition('nouveau')}
                  title="Ajouter une ligne de règlement absente de l’export ORIKA">
            + Ajouter un paiement
          </button>
        )}
      </div>

      {/* Jamais de cul-de-sac : si un filtre ne laisse rien, on dit quoi faire. */}
      {rendus.length === 0 && (
        <Vide>
          {operateurs.length === 0
            ? 'Aucun caissier sur cette journée.'
            : seulementActifs && visibles.length === 0
              ? <>Aucun caissier actif sur cette journée.{' '}
                <button className="btn gh sm" onClick={() => setSeulementActifs(false)}>
                  Afficher tous les caissiers
                </button></>
              : <>Aucun règlement ne correspond à ce filtre.{' '}
                <button className="btn gh sm"
                        onClick={() => { poserFiltreStatut('all'); poserFiltreType('all') }}>
                  Réinitialiser les filtres
                </button></>}
        </Vide>
      )}

      {badges.map(rendreBloc)}

      {sansBadge.length > 0 && (
        <>
          <Notice c="warn">
            <b>{sansBadge.length} opérateur{sansBadge.length > 1 ? 's' : ''} sans badge dans
              l’export ORIKA</b> — leurs lignes sont regroupées par nom, sous une clé
            synthétique. Ce ne sont PAS des matricules : ils restent à part pour qu’aucun
            règlement ne soit attribué au mauvais caissier.
          </Notice>
          {sansBadge.map(rendreBloc)}
        </>
      )}

      {orphelins.length > 0 && (
        <>
          <Notice c="warn">
            <b>{orphelins.length} ligne{orphelins.length > 1 ? 's' : ''} du rapport opérateur
              sans caissier identifié</b> — badge illisible et nom qui ne correspond à aucun
            règlement du jour. Elles sont montrées ici plutôt que perdues : leur écart de
            caisse et leurs enveloppes se contrôlent quand même.
          </Notice>
          {orphelins.map((o) => (
            <div className="card" key={o.id} style={{ marginBottom: 12 }}>
              <div className="card-hd">
                {/* Une ligne non rattachée n'est jamais « conforme » : même sans
                    écart, elle demande une vérification à la main. */}
                <Lamp c={severite(o.diff) === 'critique' ? 'stop' : 'warn'} />
                <Chip c="warn">NON RATTACHÉ</Chip>
                <b style={{ fontSize: 13 }}>{nomCourt(o.nom) || '—'}</b>
                <span className="sp" />
                {severite(o.diff) !== 'nul' && <Chip c={lampeEcart(o.diff)}>ÉCART {fmtEcart(o.diff)}</Chip>}
              </div>
              <PanneauRapport op={o} etat={objet(etatsRapport[o.id])}
                              seuil={reglages.seuilDeclarationEspeces} figer={lectureSeule}
                              changer={(e) => changerEtatRapport(o.id, e)} />
            </div>
          ))}
        </>
      )}
    </>
  )
}
